import { isEmpty as _isEmpty, get as _get } from "lodash";
import { IConfigBasedPrescription, ILabTest } from "../models/patients/IConfigBasedPrescription";
import {
    IAdvice,
    ICommonPrescription,
    IDiagnosis,
    IEHRSavePatientMedicalProfile, IFollowupInfo, ILabTest as IEhrSaveLabTest,
    INewConfigData,
    IPrescriptionMedication, IReferralInfo
} from "../models/patients/IEHRSavePatientMedicalProfile";
import AppUtils from "../utils/AppUtils";
import { CheckinType, PrescriptionTabModes } from "../utils/AppConstants";
import ITreatmentPlanTemplateResponseObj, {
    ITreatmentPlanTemplateSaveObj,
    TreatmentPlanFollowUpObj, TreatmentPlanMedicinesObj, TreatmentPlanReferralObj
} from "../models/ITreatmentPlanTemplateResponse";
import ModelFactory from "../models/ModelFactory";
import { EventNames } from "../utils/EventTrackerConstants";
import { IConfigBasedCurrentMedication, ICurrentMedicationConfig } from "../models/patients/IPatientHistoryCurrentMedication";
import { IEHRSavePatientHistoryCurrentMedication, ICurrentMedication } from "../models/patients/IEHRSavePatientHistoryCurrentMedication";
import IDoctorSpeciality from "../models/IDoctorSpeciality";

export class EHRRequestMapper {
    public static convertPastPrescriptionsToTemplateData(pastPrescription: any[]): ITreatmentPlanTemplateResponseObj[] {
        let suggestionItems: ITreatmentPlanTemplateResponseObj[] = [];
        suggestionItems = pastPrescription.map((prescription: any) => {
            let parsedPrescription: ITreatmentPlanTemplateResponseObj = {
                id: prescription.referenceId && prescription.referenceId.toString(),
                diagnosis: null,
                doctorId: prescription.doctorId,
                isPastPrescription: true,
                templateName: prescription.date,
                template: {
                    prescriptionAdvice: {
                        advice: prescription.prescriptionAdvice && prescription.prescriptionAdvice && prescription.prescriptionAdvice
                            && prescription.prescriptionAdvice.advice && prescription.prescriptionAdvice.advice.map((advice: any) => ({ doctorAdvice: advice.doctorAdvice })),
                        referenceId: prescription.prescriptionAdvice && prescription.prescriptionAdvice.referenceId
                    },
                    prescriptionInstructions: {
                        followupInfoSet: prescription.prescriptionInstructions && prescription.prescriptionInstructions.followupInfoSet,
                        referralInfoSet: prescription.prescriptionInstructions && prescription.prescriptionInstructions.referralInfoSet,
                        referenceId: prescription.prescriptionInstructions && prescription.prescriptionInstructions.referenceId
                    },
                    prescriptionLabTest: {
                        labTestList: prescription.prescriptionLabTest && prescription.prescriptionLabTest.labTestList
                    },
                    prescriptionMedicines: {
                        prescriptionMedicationList: prescription.prescriptionMedicines?.prescriptionMedicationList
                    },
                }
            }
            return parsedPrescription;
        })
        return suggestionItems;
    }

    public static getEHRSavePrescriptionRequestFromConfigBasedPrescription(configBasedPrescription: IConfigBasedPrescription, renderMode?: PrescriptionTabModes): IEHRSavePatientMedicalProfile | INewConfigData {
        let ehrSavePatientMedicalProfileRequest: IEHRSavePatientMedicalProfile = {
            newConfigData: { ...configBasedPrescription.currentConsultationData }
        };
        let diagnosisList: IDiagnosis[] = [], diagnosisDiseases: string[] = []
        _get(configBasedPrescription, "currentConsultationData.prescriptionDiagnosis.diagnosticsList", []).forEach((diagnosisItem: IDiagnosis) => {
            if (renderMode === PrescriptionTabModes.TREATMENT_PLAN) {
                diagnosisDiseases.push(diagnosisItem.disease.internalValue)
            } else {
                let diagnosis: IDiagnosis;
                diagnosis = { ...diagnosisItem, diagnosisType: diagnosisItem.provisional ? "PROVISIONAL" : "FINAL" }
                diagnosisList.push(diagnosis)
            }
        })
        let labTestList: IEhrSaveLabTest[] = []
        _get(configBasedPrescription, "currentConsultationData.prescriptionLabTest.labTestList", []).forEach((e: ILabTest) => {
            let labTest: IEhrSaveLabTest;
            if (e.labTestFrequency && e.labTestFrequency.unit === "STAT") {
                e.labTestFrequency.count = "0"
            }
            labTest = { ...e, medicineFrequency: e.labTestFrequency }
            labTestList.push(labTest)
        })

        let prescriptionMedicationList = _get(configBasedPrescription, "currentConsultationData.prescriptionMedicines.prescriptionMedicationList", []).map((e: IPrescriptionMedication) => {
            let prescriptionMedication: IPrescriptionMedication = { ...e }
            prescriptionMedication.medicineInstructions = []
            if (!_isEmpty(e.medicineInstructions)) {
                if (Array.isArray(e.medicineInstructions)) {
                    prescriptionMedication.medicineInstructions = e.medicineInstructions
                } else if (typeof e.medicineInstructions === "string") {
                    prescriptionMedication.medicineInstructions = [{ displayValue: "", internalValue: e.medicineInstructions }]
                }
            }
            // prescriptionMedication.medicineInstructions = e.medicineInstructions ? [{displayValue: "", internalValue: e.medicineInstructions}] : [];
            prescriptionMedication.slotList = []
            let slotArray = (_get(e, "medicineDosage", "") || "").split("-");
            if (!_isEmpty(slotArray) && slotArray.length > 1) {
                slotArray.forEach((s: string, i: number) => prescriptionMedication.slotList.push({
                    slot: ["BREAKFAST", "LUNCH", "DINNER"][i],
                    quantity: s
                }))
            }
            if (e.medicineDuration && e.medicineDuration.unit === "SOS") {
                prescriptionMedication.medicineDuration.count = "0"
            }
            if (!AppUtils.isDefined(e.dosage)) {
                prescriptionMedication.dosage = "";
            }
            return prescriptionMedication
        })
        if (ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionAdvice) {
            ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionAdvice = configBasedPrescription.currentConsultationData.prescriptionAdvice
        }
        if (ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionMedicines) {
            ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionMedicines.prescriptionMedicationList = prescriptionMedicationList
        }
        if (ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionDiagnosis && renderMode !== PrescriptionTabModes.TREATMENT_PLAN) {
            ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionDiagnosis.diagnosisList = diagnosisList
        }

        if (ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionLabTest) {
            ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionLabTest.labTestList = labTestList
        }
        if (ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionFollowUp || ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionReferral) {
            ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionInstructions = {
                followupInfoSet: [],
                referralInfoSet: [],
            }
        }
        if (!_isEmpty(ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionFollowUp)) {
            ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionInstructions.followupInfoSet.push({
                comment: _get(configBasedPrescription.currentConsultationData.prescriptionFollowUp, "followup.comment", null),
                followUp: _get(configBasedPrescription.currentConsultationData.prescriptionFollowUp, "followup.followUp", null),
                checkinType: CheckinType.FOLLOWUP
            }
            )
        }
        if (!_isEmpty(ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionReferral) && !_isEmpty(ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionReferral.referral)) {
            ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionInstructions.referralInfoSet = configBasedPrescription.currentConsultationData.prescriptionReferral.referral.map((referralItem) => {
                return {
                    comment: referralItem.comments || "",
                    doctorSpecialityCode: referralItem.doctorSpecialityCode && referralItem.doctorSpecialityCode.internalValue ? referralItem.doctorSpecialityCode.internalValue : null,
                    checkinType: CheckinType.REFERRAL
                }
            })
        }
        if (ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionReferral)
            delete ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionReferral
        if (ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionFollowUp)
            delete ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionFollowUp

        if (renderMode === PrescriptionTabModes.TREATMENT_PLAN) {
            // setting diagnosis list for Treatment Plan renderMode
            ehrSavePatientMedicalProfileRequest.newConfigData.diagnosis = diagnosisDiseases
            // delete unwanted property [prescriptionDiagnosis]
            delete ehrSavePatientMedicalProfileRequest.newConfigData.prescriptionDiagnosis
            // return data to save
            return ehrSavePatientMedicalProfileRequest.newConfigData
        }
        return ehrSavePatientMedicalProfileRequest
    }

    public static getConfigBasePrescriptionFromEHRGerPrescriptionResponse(ehrSavePatientMedicalProfile: INewConfigData): IConfigBasedPrescription {
        const labTests = { ...ehrSavePatientMedicalProfile.prescriptionLabTest }
        labTests.labTestList = (labTests.labTestList || []).map((test: IEhrSaveLabTest) => {
            if (test.labTestFrequency && test.labTestFrequency.unit === "STAT") {
                test.labTestFrequency.count = null
            }
            return test
        })
        let medicines = { ...ehrSavePatientMedicalProfile.prescriptionMedicines };
        medicines.prescriptionMedicationList = (medicines.prescriptionMedicationList || []).map((medicine: IPrescriptionMedication) => {
            if (!_isEmpty(medicine.medicineInstructions)) {
                medicine.medicineInstructions = Array.isArray(medicine.medicineInstructions) ? medicine.medicineInstructions[0].internalValue : medicine.medicineInstructions
            }
            if (medicine.medicineDuration && medicine.medicineDuration.unit === "SOS") {
                medicine.medicineDuration.count = null
            }
            return medicine
        });
        let configBasedPrescription: IConfigBasedPrescription = {
            ...ehrSavePatientMedicalProfile,
            prescriptionAdvice: _get(ehrSavePatientMedicalProfile, "prescriptionAdvice", {}),
            prescriptionMedicines: medicines,
            prescriptionLabTest: labTests,
            prescriptionFollowUp: _get(ehrSavePatientMedicalProfile, "prescriptionFollowUp", null) || _get(ehrSavePatientMedicalProfile, "prescriptionInstructions.followupInfoSet", []).map((item: any) => {
                return {
                    followup: {
                        comment: item.comment,
                        followUp: item.followUp
                    }
                }
            })[0] || {},
            prescriptionReferral: _get(ehrSavePatientMedicalProfile, "prescriptionReferral", null) || {
                referral: _get(ehrSavePatientMedicalProfile, "prescriptionInstructions.referralInfoSet", []).map((item: any) => {
                    return {
                        comments: item.comment,
                        doctorSpecialityCode: { internalValue: item.doctorSpecialityCode, displayValue: item.doctorSpecialityCode }
                    }
                }) || []
            }
        }
        return configBasedPrescription
    }

    public static getTreatmentPlanSaveObject(title: string, data: INewConfigData): ITreatmentPlanTemplateSaveObj {
        const diagnosis = data.diagnosis
        delete data.diagnosis
        return ModelFactory.createTreatmentPlanSaveObject(diagnosis, data, title)
    }

    public static getRenderObjectFromTreatmentPlanTemplate = (widget: any, tab: ICommonPrescription | INewConfigData, currentTemplate: ITreatmentPlanTemplateSaveObj, staticSpecialistData: IDoctorSpeciality[]) => {
        let tabData = { ...tab }
        let widgetData = { ...widget }
        switch (widgetData.subSectionKey) {
            case "prescriptionDiagnosis": {
                tabData = !_isEmpty(currentTemplate.diagnosis) ? {
                    diagnosticsList: currentTemplate.diagnosis.map((diag: string) => ({
                        disease: { displayValue: diag, internalValue: diag },
                        diagnosticDetails: diag
                    }))
                } : {}
                break;
            }
            case "prescriptionTemplateMapping": {
                widgetData.subSectionDescription = "Existing Treatment Plans with your selected Diagnosis"
                break;
            }
            case "prescriptionLabTest": {
                tabData.labTestList = (tabData.labTestList || []).map((test: ILabTest) => {
                    let labTest = { ...test }
                    if (labTest.labTestFrequency && labTest.labTestFrequency.unit === "STAT") {
                        labTest.labTestFrequency.count = null
                    }
                    return labTest
                })
                break;
            }
            case "prescriptionMedicines": {
                tabData.prescriptionMedicationList = (tabData.prescriptionMedicationList || []).map((meds: TreatmentPlanMedicinesObj) => {
                    let medicine: any = { ...meds }
                    if (!_isEmpty(medicine.medicineInstructions)) {
                        medicine.medicineInstructions = medicine.medicineInstructions[0].internalValue
                    }
                    if (medicine.medicineDuration && medicine.medicineDuration.unit === "SOS") {
                        medicine.medicineDuration.count = null
                    }
                    return medicine
                });
                break;
            }
            case "prescriptionAdvice": {
                tabData = !_isEmpty(tabData.advice) ? tabData : { advice: [] }
                break;
            }
            case "prescriptionFollowUp": {
                tabData = ((!_isEmpty(currentTemplate.template) &&
                    !_isEmpty(currentTemplate.template.prescriptionInstructions.followupInfoSet)) ?
                    currentTemplate.template.prescriptionInstructions.followupInfoSet : []).map((item: TreatmentPlanFollowUpObj) => {
                        return {
                            followup: {
                                comment: item.comment,
                                followUp: item.followUp
                            }
                        }
                    }).slice(-1)[0] || {}
                break;
            }
            case "prescriptionReferral": {
                tabData = (!_isEmpty(currentTemplate.template) &&
                    !_isEmpty(currentTemplate.template.prescriptionInstructions.referralInfoSet)) ?
                    {
                        referral: currentTemplate.template.prescriptionInstructions.referralInfoSet.map((item: IReferralInfo) => {
                            return {
                                comments: item.comment,
                                doctorSpecialityCode: { internalValue: item.doctorSpecialityCode, displayValue: AppUtils.getSpecialityNameFromCode(item.doctorSpecialityCode, staticSpecialistData) }
                            }
                        })
                    } : []
                break;
            }
            default: {
            }
        }

        return { tabData, widgetData }
    }

    static getRenderedWidgetTabData(renderMode: PrescriptionTabModes, ehrData: ICommonPrescription | INewConfigData, widgetData: any, currentTreatmentPlan: ITreatmentPlanTemplateSaveObj, staticSpecialistData: IDoctorSpeciality[]) {
        if (renderMode === PrescriptionTabModes.TREATMENT_PLAN) {
            return this.getRenderObjectFromTreatmentPlanTemplate(widgetData, ehrData, currentTreatmentPlan, staticSpecialistData)
        } else {
            const tabData = AppUtils.consultationDataDecider(ehrData, true, widgetData.subSectionKey);
            return { tabData, widgetData }
        }
    }

    public static getEHRPrescriptionFromTreatmentPlanTemplate = (template: ICommonPrescription, templateName: string, templateId: string, staticSpecialistData: IDoctorSpeciality[], addTrackerMixpanel?: (widget: string, value: string, templateName: string, updatingMethod: string) => void) => {
        const trackerObject = {
            updatingMethod: "Treatment_Plan",
            templateId: templateId
        }
        const convertedTemplate = {
            prescriptionLabTest: {
                labTestList:
                    (template.prescriptionLabTest.labTestList || []).map((test: IEhrSaveLabTest) => {
                        let labTest = { ...test }
                        if (labTest.labTestFrequency && labTest.labTestFrequency.unit === "STAT") {
                            labTest.labTestFrequency.count = null
                        }
                        addTrackerMixpanel && addTrackerMixpanel("labTestList", labTest.diagnosis.displayValue, templateName, EventNames.Template)
                        return { ...labTest, ...trackerObject };
                    })
            },
            prescriptionMedicines: {
                prescriptionMedicationList:
                    (template.prescriptionMedicines.prescriptionMedicationList || []).map((meds: IPrescriptionMedication) => {
                        let medicine: any = { ...meds }
                        if (!_isEmpty(medicine.medicineInstructions)) {
                            medicine.medicineInstructions = medicine.medicineInstructions[0].internalValue
                        }
                        if (medicine.medicineDuration && medicine.medicineDuration.unit === "SOS") {
                            medicine.medicineDuration.count = null
                        }
                        addTrackerMixpanel && addTrackerMixpanel("prescriptionMedicationList", meds.brand.displayValue, templateName, EventNames.Template)
                        return { ...medicine, ...trackerObject };
                    })
            },
            prescriptionAdvice: {
                advice: (!_isEmpty(template.prescriptionAdvice.advice) ? template.prescriptionAdvice.advice : []).map((adv: IAdvice) => {
                    addTrackerMixpanel && addTrackerMixpanel("advice", adv.doctorAdvice.displayValue, templateName, EventNames.Template)
                    return { ...adv, ...trackerObject };
                })
            },
            prescriptionFollowUp: {
                ...(!_isEmpty(template.prescriptionInstructions.followupInfoSet) ?
                    template.prescriptionInstructions.followupInfoSet : []).map((item: IFollowupInfo) => {
                        if (item.followUp)
                            addTrackerMixpanel && addTrackerMixpanel("followup", item.followUp.unit + (item.followUp.count || ""), templateName, EventNames.Template)
                        return {
                            followup: {
                                comment: item.comment,
                                followUp: item.followUp,
                                ...trackerObject
                            }
                        }
                    })[0] || {}
            },
            prescriptionReferral: {
                referral: (!_isEmpty(template.prescriptionInstructions.referralInfoSet) ?
                    template.prescriptionInstructions.referralInfoSet.map((item: IReferralInfo) => {
                        addTrackerMixpanel && addTrackerMixpanel("referral", item.doctorSpecialityCode, templateName, EventNames.Template)
                        return {
                            comments: item.comment,
                            doctorSpecialityCode: { internalValue: item.doctorSpecialityCode, displayValue: AppUtils.getSpecialityNameFromCode(item.doctorSpecialityCode, staticSpecialistData) },
                            ...trackerObject
                        }
                    }) : [])
            },
            prescriptionTemplateMapping: {
                templateIds: [templateId]
            }
        }
        return convertedTemplate
    }

    public static getTemplateDataObjectToSave = (configBasedPrescription: IConfigBasedPrescription, title: string): ITreatmentPlanTemplateSaveObj => {
        const sectionsData = EHRRequestMapper.getEHRSavePrescriptionRequestFromConfigBasedPrescription(configBasedPrescription, PrescriptionTabModes.TREATMENT_PLAN)
        return EHRRequestMapper.getTreatmentPlanSaveObject(title, sectionsData as INewConfigData)
    }

    public static getEHRSaveHistoryCurrentMedicationFromConfigBasedMedication(configBasedMedication: IConfigBasedCurrentMedication): IEHRSavePatientHistoryCurrentMedication {
        let currentMedicationList = (_get(configBasedMedication, "medicationList", []) as []).map((e: ICurrentMedication) => {
            let prescriptionMedication: ICurrentMedication = { ...e };
            prescriptionMedication.slotList = []
            let slotArray = (_get(e, "medicineDosage", "") || "").split("-");
            if (!_isEmpty(slotArray) && slotArray.length > 1) {
                slotArray.forEach((s: string, i: number) => prescriptionMedication.slotList.push({
                    slot: ["BREAKFAST", "LUNCH", "DINNER"][i],
                    quantity: s
                }))
                prescriptionMedication.breakfastQuantity = slotArray[0];
                prescriptionMedication.lunchQuantity = slotArray[1];
                prescriptionMedication.dinnerQuantity = slotArray[2];
            }
            if (!AppUtils.isDefined(e.dosage)) {
                prescriptionMedication.dosage = "";
            }
            prescriptionMedication.productName = e.brand;

            return prescriptionMedication
        })
        return { medicationList: currentMedicationList }
    }
}

